/**
 * Right panel of the View tab.
 *
 * Per planchat.md §13 the Inspector is the application's information centre:
 * its content follows the selected node rather than being a fixed properties
 * sheet. With nothing selected it falls back to the viewer controls, which is
 * what the mockup shows.
 *
 * Values come from three sources and the UI never blurs them:
 *   - the NIfTI header, via `read_nifti_header`;
 *   - the volume NiiVue actually loaded (min/max, measured mask volume);
 *   - `CaseNode.meta`, which the backend fills from real pipeline outputs.
 * Anything none of those supply is rendered as an explicit "not recorded" or
 * "not computed", never as a number.
 */

import * as React from "react";
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge, Button, Checkbox, Input, Panel, Select } from "@/components/ui/primitives";
import { cn, formatDuration } from "@/lib/utils";
import { getResultsSummary, openPath, readNiftiHeader, revealPath } from "@/ipc/api";
import type { CaseNode, NiftiHeaderInfo, ResultsSummary } from "@/ipc/contract";
import { formatAffine, xformName } from "@/tools/header";
import { selectTool } from "@/tools/actions";
import { appActions, useJob } from "@/store/appStore";

// NiiVue's DRAG_MODE is a runtime enum; importing it here would pull the whole
// library into this panel and defeat the viewer's dynamic import, so the four
// values we need are spelled out. Source: @niivue/niivue DRAG_MODE.
const DRAG_MODE_MEASUREMENT = 2 as never;
const DRAG_MODE_PAN = 3 as never;
const DRAG_MODE_CROSSHAIR = 8 as never;
const DRAG_MODE_WINDOWING = 9 as never;
import { forEachInstance } from "@/viewer/nvRegistry";
import { deriveQcReport, type CheckStatus } from "@/viewer/qc";
import {
  nodeAbsPath,
  useManifest,
  useSelectedNode,
  useViewerStore,
} from "@/viewer/viewerStore";
import { OVERLAY_COLORMAPS, OVERLAY_COLORMAP_LABELS, type ViewPlane } from "@/viewer/types";

const NIFTI_RE = /\.nii(\.gz)?$/i;

/** Voxel axis that advances when you page through the named plane. */
const PLANE_AXIS: Record<ViewPlane, 0 | 1 | 2> = {
  sagittal: 0,
  coronal: 1,
  axial: 2,
  multiplanar: 2,
  render: 2,
};

export function Inspector() {
  const node = useSelectedNode();
  return (
    <Panel title="Inspector" className="h-full" bodyClassName="text-xs">
      {node ? <NodeInspector node={node} /> : <ViewerControls />}
    </Panel>
  );
}

// ------------------------------------------------------------- node views ---

function NodeInspector({ node }: { node: CaseNode }) {
  const manifest = useManifest();
  const path = nodeAbsPath(manifest, node);
  const [header, setHeader] = React.useState<NiftiHeaderInfo | null>(null);
  const [headerError, setHeaderError] = React.useState<string | null>(null);
  const store = useViewerStore();

  const isNifti = NIFTI_RE.test(node.relPath);

  React.useEffect(() => {
    setHeader(null);
    setHeaderError(null);
    if (!path || !isNifti) return;
    let cancelled = false;
    void readNiftiHeader(path)
      .then((info) => !cancelled && setHeader(info))
      .catch(() => !cancelled && setHeaderError("Header could not be read."));
    return () => {
      cancelled = true;
    };
  }, [path, isNifti]);

  // Voxel extremes are a property of the loaded data, so they are only known
  // for the volume that is actually on screen.
  const liveStats =
    store.baseNodeId === node.id
      ? store.baseStats
      : store.overlayNodeIds[0] === node.id
        ? store.overlayStats
        : null;

  const common = (
    <>
      <Row label="File" value={node.relPath.split("/").pop() ?? node.relPath} title={node.relPath} />
      <Row label="Space" value={node.space} />
      <Row label="Origin" value={node.origin === "input" ? "Input" : "Pipeline output"} />
    </>
  );

  return (
    <div className="flex flex-col pb-4">
      <header className="border-b border-ads-border px-3 py-2">
        <p className="text-[11px] uppercase tracking-wide text-ads-muted">{kindLabel(node.kind)}</p>
        <p className="truncate text-sm font-semibold text-ads-text">{node.label}</p>
      </header>

      {node.kind === "transform" ? (
        <TransformProperties node={node} common={common} />
      ) : node.kind === "mask" ? (
        <MaskProperties node={node} header={header} liveStats={liveStats} common={common} />
      ) : isNifti ? (
        <ImageProperties node={node} header={header} liveStats={liveStats} common={common} />
      ) : (
        <Section title="Properties">{common}</Section>
      )}

      {headerError ? (
        <p className="px-3 pb-2 text-[11px] text-amber-600">{headerError}</p>
      ) : null}

      <div className="flex flex-wrap gap-1.5 px-3 pt-1">
        {node.kind === "image" ? (
          <Button size="sm" onClick={() => store.setBaseNode(node.id)}>
            View as Base
          </Button>
        ) : null}
        {node.kind === "mask" || node.kind === "atlas" ? (
          <Button
            size="sm"
            onClick={() => {
              if (!store.overlayNodeIds.includes(node.id)) store.toggleOverlayNode(node.id);
              store.setOption("overlayVisible", true);
            }}
          >
            View Overlay
          </Button>
        ) : null}
        {node.kind === "mask" ? (
          <Button
            size="sm"
            title="Open Quantify and measure this mask"
            onClick={() => {
              if (!store.overlayNodeIds.includes(node.id)) store.toggleOverlayNode(node.id);
              store.setOption("overlayVisible", true);
              selectTool("q.coreVolume");
            }}
          >
            Quantify
          </Button>
        ) : null}
        {node.kind === "report" || node.kind === "figure" || node.kind === "table" ? (
          <Button size="sm" onClick={() => path && void openPath(path).catch(() => undefined)}>
            Open
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          onClick={() => path && void revealPath(path).catch(() => undefined)}
        >
          Show in Folder
        </Button>
      </div>
    </div>
  );
}

function ImageProperties({
  node,
  header,
  liveStats,
  common,
}: {
  node: CaseNode;
  header: NiftiHeaderInfo | null;
  liveStats: ReturnType<typeof useViewerStore.getState>["baseStats"];
  common: React.ReactNode;
}) {
  return (
    <Section title="Image">
      {common}
      <Row label="Dimensions" value={header ? header.dim.join(" × ") : PENDING} />
      <Row
        label="Spacing"
        value={header ? `${header.pixdim.slice(0, 3).map((v) => round(v, 3)).join(" × ")} mm` : PENDING}
      />
      <Row label="Orientation" value={header?.orientation ?? PENDING} />
      <Row label="Volumes" value={header ? String(header.volumeCount ?? 1) : PENDING} />
      <Row label="Datatype" value={header?.datatype ?? PENDING} />
      <Row
        label="qform"
        value={header ? `${header.qformCode} (${xformName(header.qformCode)})` : PENDING}
      />
      <Row
        label="sform"
        value={header ? `${header.sformCode} (${xformName(header.sformCode)})` : PENDING}
      />
      <Row label="Affine" value={header?.affine ? formatAffine(header.affine) : PENDING} />
      <Row label="Compressed" value={header ? (header.isCompressed ? "Yes" : "No") : PENDING} />
      <Row
        label="Min / Max"
        value={liveStats ? `${round(liveStats.min, 2)} / ${round(liveStats.max, 2)}` : null}
        note={liveStats ? undefined : "Load this volume in the viewer to measure it"}
      />
      <Row
        label="Mean / Std"
        value={liveStats && liveStats.finiteVox > 0 ? `${round(liveStats.mean, 2)} / ${round(liveStats.std, 2)}` : null}
        note={liveStats ? undefined : "Load this volume in the viewer to measure it"}
      />
      <Row
        label="Median"
        value={liveStats ? round(liveStats.median ?? Number.NaN, 2) : null}
        note={liveStats ? undefined : "Load this volume in the viewer to measure it"}
      />
      <Row label="Derived" value={node.derived ? "Yes — pipeline output" : "No — source data"} />
      {Object.entries(node.meta).map(([key, value]) => (
        <Row key={key} label={key} value={value == null ? null : String(value)} />
      ))}
    </Section>
  );
}

function MaskProperties({
  node,
  header,
  liveStats,
  common,
}: {
  node: CaseNode;
  header: NiftiHeaderInfo | null;
  liveStats: ReturnType<typeof useViewerStore.getState>["baseStats"];
  common: React.ReactNode;
}) {
  const meta = node.meta;
  return (
    <Section title="Mask">
      {common}
      <Row label="Model" value={metaString(meta.model)} note="From the case manifest" />
      <Row label="Input" value={metaString(meta.input)} note="Not recorded by the pipeline" />
      <Row label="Threshold" value={metaString(meta.threshold)} note="Not recorded by the pipeline" />
      <Row label="Dimensions" value={header ? header.dim.join(" × ") : PENDING} />
      <Row
        label="Volume"
        value={liveStats ? `${round(liveStats.volumeMl, 2)} mL` : null}
        note={liveStats ? "Measured from the loaded mask" : "Load this mask as an overlay to measure it"}
      />
      <Row
        label="Non-zero voxels"
        value={liveStats ? liveStats.nonZeroVox.toLocaleString() : null}
        note={liveStats ? undefined : "Load this mask as an overlay to measure it"}
      />
      <Row
        label="Centroid"
        value={
          liveStats?.centroidVox
            ? liveStats.centroidVox.map((n) => n.toFixed(1)).join(", ") + " vox"
            : null
        }
        note={liveStats?.centroidVox ? "Centre of mass of voxels > 0" : "Load this mask as an overlay"}
      />
      <Row
        label="Bounding box"
        value={
          liveStats?.bboxVox
            ? `${liveStats.bboxVox.min[0]}–${liveStats.bboxVox.max[0]} × ${liveStats.bboxVox.min[1]}–${liveStats.bboxVox.max[1]} × ${liveStats.bboxVox.min[2]}–${liveStats.bboxVox.max[2]}`
            : null
        }
        note={liveStats?.bboxVox ? "Inclusive voxel index box" : "Load this mask as an overlay"}
      />
      {/* Connected-component count still needs SimpleITK (Segmentation → Component Count). */}
      <Row label="Components" value={null} note="Use Segmentation → Component Count" />
    </Section>
  );
}

function TransformProperties({ node, common }: { node: CaseNode; common: React.ReactNode }) {
  const manifest = useManifest();
  const job = useJob();
  const file = node.relPath.split("/").pop() ?? "";

  // The method is read off the file name the pipeline writes, not guessed:
  // `_aff_` is the ANTs affine matrix, `_warp_` the SyN displacement field.
  const method = /_aff/i.test(file)
    ? "Affine (ANTs)"
    : /warp/i.test(file)
      ? "SyN warp (ANTs)"
      : null;

  const moving = node.label.includes("→") ? node.label.split("→")[0].trim() : null;
  const registration = job?.stages.find((stage) => stage.id === "registration");
  const affineFile = manifest?.transforms.find((t) => /_aff/i.test(t.relPath))?.relPath.split("/").pop();
  const warpFile = manifest?.transforms.find((t) => /warp/i.test(t.relPath))?.relPath.split("/").pop();

  return (
    <Section title="Registration">
      {common}
      <Row label="Fixed" value={node.space === "unknown" ? null : node.space} note="Target space" />
      <Row label="Moving" value={moving} note="Not recorded by the pipeline" />
      <Row label="Method" value={method} note="Inferred from the output file name" />
      <Row label="Interpolation" value={metaString(node.meta.interpolation)} note="Not recorded by the pipeline" />
      <Row
        label="Status"
        value={registration ? registration.state : "Written"}
        note={registration ? undefined : "The file exists in the case outputs"}
      />
      <Row
        label="Runtime"
        value={registration?.durationSec != null ? formatDuration(registration.durationSec) : null}
        note="Available after a run in this session"
      />
      <Row label="Affine" value={affineFile ?? null} note="No affine file in this case" />
      <Row label="Warp" value={warpFile ?? null} note="No warp file in this case" />
    </Section>
  );
}

// -------------------------------------------------------- viewer controls ---

function ViewerControls() {
  const manifest = useManifest();
  const store = useViewerStore();
  const [results, setResults] = React.useState<ResultsSummary | null>(null);

  React.useEffect(() => {
    if (!manifest) {
      setResults(null);
      return;
    }
    let cancelled = false;
    void getResultsSummary(manifest.caseId)
      .then((summary) => !cancelled && setResults(summary))
      .catch(() => !cancelled && setResults(null));
    return () => {
      cancelled = true;
    };
  }, [manifest]);

  const qc = React.useMemo(() => deriveQcReport(manifest, results), [manifest, results]);

  const plane = store.panes[0] ?? "axial";
  const axis = PLANE_AXIS[plane];
  const dims = store.baseStats?.dims ?? null;
  const extent = dims ? dims[axis] : null;
  const current = store.location ? Math.round(store.location.vox[axis]) : null;

  return (
    <div className="flex flex-col pb-4">
      <Section title="Overlay">
        <SliderRow
          label="Opacity"
          value={store.overlayOpacity}
          min={0}
          max={1}
          step={0.05}
          display={`${Math.round(store.overlayOpacity * 100)}%`}
          onChange={(v) => store.setOption("overlayOpacity", v)}
        />
        <LabelledControl label="Color Map">
          <Select
            className="h-7 text-xs"
            value={store.overlayColormap}
            onChange={(e) => store.setOption("overlayColormap", e.target.value)}
          >
            {OVERLAY_COLORMAPS.map((cm) => (
              <option key={cm} value={cm}>
                {OVERLAY_COLORMAP_LABELS[cm] ?? cm}
              </option>
            ))}
          </Select>
        </LabelledControl>
        <LabelledControl label="Threshold">
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-ads-muted">Min</span>
            <Input
              className="h-7 w-16 text-xs"
              type="number"
              step={0.05}
              value={store.thresholdMin}
              onChange={(e) => store.setOption("thresholdMin", Number(e.target.value))}
            />
            <span className="text-[11px] text-ads-muted">Max</span>
            <Input
              className="h-7 w-16 text-xs"
              type="number"
              step={0.05}
              value={store.thresholdMax}
              onChange={(e) => store.setOption("thresholdMax", Number(e.target.value))}
            />
          </div>
        </LabelledControl>
      </Section>

      <Section title="Slice & Navigation">
        <LabelledControl label={`Location (${capitalise(plane)})`}>
          {current != null && extent ? (
            <div className="flex items-center gap-2">
              <span className="w-16 shrink-0 tabular-nums text-ads-text">
                {current} / {extent}
              </span>
              <input
                type="range"
                className="h-1 flex-1 accent-ads-accent"
                min={0}
                max={Math.max(0, extent - 1)}
                value={current}
                onChange={(e) => store.gotoSlice(axis, Number(e.target.value))}
              />
            </div>
          ) : (
            <span className="text-[11px] text-ads-faint">No volume loaded</span>
          )}
        </LabelledControl>

        <div className="flex items-center gap-1 px-3 py-1">
          <NavButton label="First slice" onClick={() => store.gotoSlice(axis, 0)} disabled={!extent}>
            <ChevronFirst className="h-3.5 w-3.5" />
          </NavButton>
          <NavButton label="Previous slice" onClick={() => store.stepSlice(axis, -1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </NavButton>
          <NavButton label="Next slice" onClick={() => store.stepSlice(axis, 1)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </NavButton>
          <NavButton
            label="Last slice"
            onClick={() => extent && store.gotoSlice(axis, extent - 1)}
            disabled={!extent}
          >
            <ChevronLast className="h-3.5 w-3.5" />
          </NavButton>
          <Button
            size="sm"
            className="ml-auto"
            disabled={!extent}
            onClick={() => extent && store.gotoSlice(axis, Math.floor(extent / 2))}
          >
            Center
          </Button>
        </div>

        <LabelledControl
          label="Slice Thickness"
          hint="Display setting only — the viewer draws one voxel per slice."
        >
          <Select
            className="h-7 text-xs"
            value={String(store.sliceThicknessMm)}
            onChange={(e) => store.setOption("sliceThicknessMm", Number(e.target.value))}
          >
            {[1, 2, 3, 5].map((mm) => (
              <option key={mm} value={mm}>
                {mm.toFixed(1)} mm
              </option>
            ))}
          </Select>
        </LabelledControl>
      </Section>

      <Section title="Synchronisation">
        <SwitchRow
          label="Link Views"
          checked={store.linked}
          onChange={(v) => store.setOption("linked", v)}
        />
        <SwitchRow
          label="Crosshairs"
          checked={store.crosshair}
          onChange={(v) => store.setOption("crosshair", v)}
        />
        <SwitchRow
          label="Follow Cursor"
          checked={store.followCursor}
          onChange={(v) => {
            store.setOption("followCursor", v);
            // NiiVue moves the crosshair on click by default; "follow cursor"
            // is the hover variant, which is a mouse-event config change.
            forEachInstance((nv) => {
              nv.opts.mouseEventConfig = v
                ? {
                    leftButton: {
                      primary: DRAG_MODE_CROSSHAIR,
                      withShift: DRAG_MODE_MEASUREMENT,
                      withCtrl: DRAG_MODE_WINDOWING,
                    },
                    rightButton: DRAG_MODE_WINDOWING,
                    centerButton: DRAG_MODE_PAN,
                  }
                : undefined;
            });
          }}
        />
      </Section>

      <Section title="Anatomical Labels">
        <div className="flex items-center gap-6 px-3 py-1">
          <label className="flex items-center gap-1.5 text-ads-text">
            <Checkbox
              checked={store.showLabels}
              onChange={(e) => store.setOption("showLabels", e.target.checked)}
            />
            Show Labels
          </label>
          <label className="flex items-center gap-1.5 text-ads-text">
            <Checkbox
              checked={store.showScale}
              onChange={(e) => store.setOption("showScale", e.target.checked)}
            />
            Show Scale
          </label>
        </div>
      </Section>

      <Section title="Measurements">
        <LabelledControl label="Active Tool">
          <Select
            className="h-7 text-xs"
            defaultValue="none"
            onChange={(e) => {
              const tool = e.target.value;
              forEachInstance((nv) => {
                nv.opts.dragMode =
                  tool === "distance"
                    ? nv.dragModes.measurement
                    : tool === "angle"
                      ? nv.dragModes.angle
                      : nv.dragModes.contrast;
              });
            }}
          >
            <option value="none">None</option>
            <option value="distance">Distance</option>
            <option value="angle">Angle</option>
          </Select>
        </LabelledControl>
        <div className="flex items-center justify-between px-3 py-1">
          {/* The mockup also shows pen / ellipse / rectangle / profile tools.
              NiiVue 0.68 exposes distance and angle only, and drawing an ROI
              would imply an editing pipeline v1 does not have. */}
          <span className="text-[11px] text-ads-faint">ROI and profile tools: not available in v1</span>
          <Button size="sm" variant="outline" onClick={() => forEachInstance((nv) => nv.clearMeasurements())}>
            Clear
          </Button>
        </div>
      </Section>

      <Section title="QC Status">
        <QcRow label="Overall" status={qc.overall === "pass" ? "pass" : qc.overall === "warning" ? "warning" : "unavailable"} />
        {qc.checks.slice(0, 4).map((check) => (
          <QcRow key={check.id} label={check.label} status={check.status} title={check.detail} />
        ))}
        <div className="px-3 py-2">
          <Button
            size="sm"
            className="w-full"
            onClick={() => {
              appActions.setDockTab("qc");
              appActions.togglePanel("bottom", false);
            }}
          >
            View QC Report
          </Button>
        </div>
      </Section>
    </div>
  );
}

// -------------------------------------------------------------- fragments ---

const PENDING = null;

function kindLabel(kind: CaseNode["kind"]): string {
  const labels: Record<CaseNode["kind"], string> = {
    image: "Image",
    mask: "Mask",
    transform: "Transform",
    atlas: "Atlas",
    table: "Table",
    report: "Report",
    figure: "Figure",
  };
  return labels[kind];
}

function metaString(value: string | number | null | undefined): string | null {
  if (value == null || value === "") return null;
  return String(value);
}

function round(value: number, digits: number): string {
  return Number.isFinite(value) ? String(Number(value.toFixed(digits))) : "—";
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-ads-border py-1.5 last:border-b-0">
      <h4 className="px-3 py-1 text-[11px] font-semibold text-ads-accent">{title}</h4>
      {children}
    </section>
  );
}

/**
 * One property. A null `value` renders the `note` instead — the difference
 * between "we know it is empty" and "we never computed it" has to survive.
 */
function Row({
  label,
  value,
  note,
  title,
}: {
  label: string;
  value: string | null;
  note?: string;
  title?: string;
}) {
  return (
    <div className="flex items-baseline gap-2 px-3 py-[3px]" title={title}>
      <span className="w-28 shrink-0 text-ads-muted">{label}</span>
      {value != null ? (
        <span className="min-w-0 flex-1 truncate text-ads-text" title={value}>
          {value}
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-ads-faint italic" title={note}>
          {note ?? "—"}
        </span>
      )}
    </div>
  );
}

function LabelledControl({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-3 py-1">
      <div className="flex items-center gap-2">
        <span className="w-28 shrink-0 text-ads-muted">{label}</span>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      {hint ? <p className="pl-30 pt-0.5 text-[11px] text-ads-faint">{hint}</p> : null}
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-1">
      <span className="w-28 shrink-0 text-ads-muted">{label}</span>
      <input
        type="range"
        className="h-1 flex-1 accent-ads-accent"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="w-10 shrink-0 text-right tabular-nums text-ads-text">{display}</span>
    </div>
  );
}

function SwitchRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-1">
      <span className="text-ads-text">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative h-4 w-8 shrink-0 rounded-full transition-colors duration-100",
          checked ? "bg-ads-accent" : "bg-ads-border-strong",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left] duration-100",
            checked ? "left-4.5" : "left-0.5",
          )}
        />
      </button>
    </div>
  );
}

function QcRow({ label, status, title }: { label: string; status: CheckStatus; title?: string }) {
  const tone = status === "pass" ? "green" : status === "warning" ? "amber" : "neutral";
  const text = status === "pass" ? "PASS" : status === "warning" ? "WARNING" : status === "unavailable" ? "N/A" : "—";
  return (
    <div className="flex items-center justify-between px-3 py-[3px]" title={title}>
      <span className="min-w-0 truncate text-ads-text">{label}</span>
      <Badge tone={tone}>{text}</Badge>
    </div>
  );
}

function NavButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded border border-ads-border-strong bg-ads-surface text-ads-muted hover:bg-ads-hover disabled:opacity-40"
    >
      {children}
    </button>
  );
}
